// StressOverlayPlugin - Read-only stress mark overlay rendering
// Renders stress marks as absolutely positioned elements above lyrics text
// Uses Range pooling + viewport virtualization for performance (Scope 05)
// Respects user preference: preferences.feedback.stress.enabled (Scope 07)

import { useEffect, useRef, useState, useCallback } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEditorPreferences } from '../../../hooks/useEditorPreferences'
import { $getRoot, type LexicalNode, COMMAND_PRIORITY_LOW } from 'lexical'
import { autoUpdate } from '@floating-ui/dom'
import { $isLyricsTextNode, $getAllStressPatterns, LyricsTextNode } from '../nodes/LyricsTextNode'
import type { StressPattern } from '../types/prosody'
import {
  STRESS_OVERLAY_TAG,
  SET_LINE_STRESS_METADATA_COMMAND,
  type SetLineStressMetadataPayload,
} from './StressDataPlugin'
import StressMarkSymbols from '../components/StressMarkSymbols'
import AccentMark from '../components/AccentMark'
import StressCountBadge from '../components/StressCountBadge'
import type { WordBreakdown } from '../utils/stressTooltipContent'
import { RangePool } from '../utils/RangePool'
import { useVirtualizedMarks } from '../hooks/useVirtualizedMarks'

// ============================================================================
// Design Token Constants
// ============================================================================

/**
 * Offset from syllable bounding rect top edge for mark positioning.
 * Sourced from: docs/design/feedback_system/design_tokens.json
 * Path: components.accentMark.position.boundingRectOffset
 *
 * This differs from the CSS `top: -17px` in the style guide because:
 * - CSS positioning is relative to a wrapper element with transform: translateX(-50%)
 * - Bounding rect positioning calculates directly from syllable coordinates
 *
 * Mark height is 10px, so -5px places mark's bottom ~5px above text top.
 */
const ACCENT_MARK_BOUNDING_RECT_OFFSET = -5

// ============================================================================
// Types for Overlay Rendering
// ============================================================================

/**
 * Descriptor for a single stress mark to render
 *
 * Stores position, syllable data, and override status for rendering.
 * Extended with nodeKey/word/syllableIndex for interaction plugin support.
 */
interface StressMarkDescriptor {
  /** Unique key for React rendering (nodeKey:wordStart:syllableIndex) */
  key: string
  /** X position relative to editor root */
  x: number
  /** Y position relative to editor root */
  y: number
  /** Syllable text (for debugging) */
  text: string
  /** Whether this syllable is stressed */
  stressed: boolean
  /** Stress level (0=unstressed, 1=primary, 2=secondary) */
  stressLevel: number
  /** Whether this was manually overridden */
  overridden: boolean
  /** Contextual override reasoning (e.g., "contextual_noun") */
  reasoning?: string
  /** Confidence score for contextual overrides */
  confidence?: number
  /** Node key for interaction plugin */
  nodeKey: string
  /** Word this syllable belongs to */
  word: string
  /** Index of syllable within word */
  syllableIndex: number
}

/**
 * Map of node keys to their mark descriptors
 * Track marks per node for efficient cleanup when nodes change
 */
type MarksByNodeKey = Map<string, StressMarkDescriptor[]>

// ============================================================================
// Plugin Component
// ============================================================================

/**
 * StressOverlayPlugin - Renders read-only stress marks above lyrics text
 *
 * Architecture:
 * - ONE overlay container mounted inside Lexical root
 * - Tracks marksByNodeKey, clears entries when node disappears or text changes
 * - Listens for LyricsTextNode mutations + STRESS_OVERLAY_TAG updates
 * - Positions calculated via pooled Range API with batched DOM reads
 * - Uses autoUpdate from @floating-ui/dom with requestAnimationFrame guard
 * - Viewport virtualization filters to visible marks only
 *
 * Performance optimizations (Scope 05):
 * - RangePool: Reuses Range objects to reduce GC pressure
 * - Batched DOM reads: All getBoundingClientRect() calls in one phase
 * - useVirtualizedMarks: Only renders marks in viewport + buffer
 */
export default function StressOverlayPlugin() {
  const [editor] = useLexicalComposerContext()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const overlayContainerRef = useRef<HTMLDivElement | null>(null)
  const [containerMounted, setContainerMounted] = useState(false)
  const [allMarks, setAllMarks] = useState<StressMarkDescriptor[]>([])
  const marksByNodeKeyRef = useRef<MarksByNodeKey>(new Map())
  const rafScheduledRef = useRef(false)
  const rangePoolRef = useRef<RangePool>(new RangePool(50))
  const [lineMetadata, setLineMetadata] = useState<SetLineStressMetadataPayload['metadata']>({})
  const [wordBreakdowns, setWordBreakdowns] = useState<Map<string, WordBreakdown[]>>(new Map())
  const { preferences } = useEditorPreferences()

  // Viewport virtualization - filter to visible marks
  const visibleMarks = useVirtualizedMarks(allMarks, overlayContainerRef, 100)

  // Cleanup range pool on unmount
  useEffect(() => {
    const pool = rangePoolRef.current
    return () => {
      pool.clear()
    }
  }, [])

  // Recalculate mark positions based on current editor state
  // Batches all DOM reads (getBoundingClientRect) before state updates
  const recalculateMarks = useCallback(() => {
    if (rafScheduledRef.current) return

    rafScheduledRef.current = true
    requestAnimationFrame(() => {
      rafScheduledRef.current = false

      // Start timing for performance metrics
      const startTime = performance.now()

      const rootElement = editor.getRootElement()
      if (!rootElement || !containerRef.current) {
        return
      }

      // PHASE 1: Collect all data we need from editor state (no DOM reads yet)
      interface NodeData {
        nodeKey: string
        textContent: string
        patterns: Record<string, StressPattern>
        words: Array<{
          raw: string
          normalized: string
          startIndex: number
          pattern: StressPattern
        }>
      }
      const nodeDataList: NodeData[] = []

      editor.getEditorState().read(() => {
        const root = $getRoot()

        // Recursive function to collect all LyricsTextNodes
        const collectTextNodes = (node: LexicalNode): LyricsTextNode[] => {
          const textNodes: LyricsTextNode[] = []

          if ($isLyricsTextNode(node)) {
            textNodes.push(node)
          }

          if ('getChildren' in node && typeof node.getChildren === 'function') {
            const children = node.getChildren()
            for (const child of children) {
              textNodes.push(...collectTextNodes(child))
            }
          }

          return textNodes
        }

        const textNodes = collectTextNodes(root)

        // Extract data from each node (still in Lexical read context)
        for (const node of textNodes) {
          const nodeKey = node.getKey()
          const textContent = node.getTextContent()
          const stressPatterns = $getAllStressPatterns(node)

          if (!textContent.trim() || Object.keys(stressPatterns).length === 0) {
            continue
          }

          const words: NodeData['words'] = []
          const wordList = textContent.split(/\s+/).filter(w => w.length > 0)
          let searchStart = 0

          for (const rawWord of wordList) {
            const normalizedWord = rawWord.toLowerCase().replace(/[^a-z']/g, '')
            if (!normalizedWord) continue

            const pattern = stressPatterns[normalizedWord]
            if (!pattern) continue

            const wordIndex = textContent.indexOf(rawWord, searchStart)
            if (wordIndex === -1) continue

            searchStart = wordIndex + rawWord.length

            words.push({
              raw: rawWord,
              normalized: normalizedWord,
              startIndex: wordIndex,
              pattern,
            })
          }

          if (words.length > 0) {
            nodeDataList.push({
              nodeKey,
              textContent,
              patterns: stressPatterns,
              words,
            })
          }
        }
      })

      // PHASE 2: Batch all DOM reads
      // Get root rect once (outside the loop)
      const rootRect = rootElement.getBoundingClientRect()

      // Collect all syllable rects in one pass
      interface SyllableMeasurement {
        nodeKey: string
        word: string
        wordStartOffset: number  // Character offset where this word instance starts
        syllableIndex: number
        syllable: StressPattern['syllables'][0]
        rect: DOMRect | null
      }
      const measurements: SyllableMeasurement[] = []
      const pool = rangePoolRef.current

      for (const nodeData of nodeDataList) {
        const textNodeDom = editor.getElementByKey(nodeData.nodeKey)
        if (!textNodeDom) continue

        const textNode = textNodeDom.firstChild
        if (!textNode || textNode.nodeType !== Node.TEXT_NODE) continue

        for (const wordData of nodeData.words) {
          let currentOffset = wordData.startIndex

          for (let i = 0; i < wordData.pattern.syllables.length; i++) {
            const syllable = wordData.pattern.syllables[i]

            try {
              const range = pool.acquire()
              range.setStart(textNode, currentOffset)
              range.setEnd(textNode, currentOffset + syllable.text.length)

              const rect = range.getBoundingClientRect()
              measurements.push({
                nodeKey: nodeData.nodeKey,
                word: wordData.normalized,
                wordStartOffset: wordData.startIndex,
                syllableIndex: i,
                syllable,
                rect,
              })

              pool.release(range)
              currentOffset += syllable.text.length
            } catch {
              // Range creation can fail if offsets are invalid
              measurements.push({
                nodeKey: nodeData.nodeKey,
                word: wordData.normalized,
                wordStartOffset: wordData.startIndex,
                syllableIndex: i,
                syllable,
                rect: null,
              })
            }
          }
        }
      }

      // PHASE 3: Build word breakdowns for tooltip data
      const newWordBreakdowns = new Map<string, WordBreakdown[]>()

      for (const nodeData of nodeDataList) {
        const words: WordBreakdown[] = nodeData.words.map(wordData => ({
          word: wordData.raw,
          syllables: wordData.pattern.syllables.map(syl => ({
            text: syl.text,
            stressed: syl.stressed,
          })),
        }))

        if (words.length > 0) {
          newWordBreakdowns.set(nodeData.nodeKey, words)
        }
      }

      // PHASE 4: Build descriptors from measurements (no more DOM reads)
      const newMarksByNodeKey: MarksByNodeKey = new Map()
      const newAllMarks: StressMarkDescriptor[] = []

      for (const measurement of measurements) {
        if (!measurement.rect) continue

        // Calculate position from viewport-relative rect
        // Design token: accentMark.position.left = 50% (center on syllable)
        // Mark is 8px wide, so offset by half to center: -4px
        const syllableCenter = measurement.rect.width / 2
        const markHalfWidth = 4  // 8px width / 2
        const xPosition = measurement.rect.left - rootRect.left + syllableCenter - markHalfWidth

        // Position mark just above the text's top edge
        // Uses documented design token: ACCENT_MARK_BOUNDING_RECT_OFFSET
        // See: docs/design/feedback_system/design_tokens.json → components.accentMark.position.boundingRectOffset
        const yPosition = measurement.rect.top - rootRect.top + ACCENT_MARK_BOUNDING_RECT_OFFSET

        const descriptor: StressMarkDescriptor = {
          // Key uses wordStartOffset to ensure uniqueness when same word appears multiple times
          key: `${measurement.nodeKey}:${measurement.wordStartOffset}:${measurement.syllableIndex}`,
          x: xPosition,
          y: yPosition,
          text: measurement.syllable.text,
          stressed: measurement.syllable.stressed,
          stressLevel: 'stressLevel' in measurement.syllable
            ? (measurement.syllable.stressLevel as number)
            : (measurement.syllable.stressed ? 1 : 0),
          overridden: measurement.syllable.overridden,
          reasoning: 'reasoning' in measurement.syllable && typeof measurement.syllable.reasoning === 'string'
            ? measurement.syllable.reasoning
            : undefined,
          confidence: measurement.syllable.confidence,
          nodeKey: measurement.nodeKey,
          word: measurement.word,
          syllableIndex: measurement.syllableIndex,
        }

        // Group by node key
        if (!newMarksByNodeKey.has(measurement.nodeKey)) {
          newMarksByNodeKey.set(measurement.nodeKey, [])
        }
        newMarksByNodeKey.get(measurement.nodeKey)!.push(descriptor)
        newAllMarks.push(descriptor)
      }

      // PHASE 5: Update state (batch state updates)
      marksByNodeKeyRef.current = newMarksByNodeKey
      setAllMarks(newAllMarks)
      setWordBreakdowns(newWordBreakdowns)

      // Performance logging - always log in development for metrics capture
      if (process.env.NODE_ENV === 'development') {
        const renderTime = performance.now() - startTime
        console.log(`[StressOverlayPlugin] Recalculated ${newAllMarks.length} marks across ${newMarksByNodeKey.size} nodes in ${renderTime.toFixed(2)}ms`)
      }
    })
  }, [editor])

  // Register mutation listener for LyricsTextNode changes
  useEffect(() => {
    return editor.registerMutationListener(
      LyricsTextNode,
      (mutations) => {
        let needsRecalc = false

        for (const [nodeKey, mutation] of mutations) {
          if (mutation === 'destroyed') {
            marksByNodeKeyRef.current.delete(nodeKey)
            needsRecalc = true
          } else if (mutation === 'updated' || mutation === 'created') {
            needsRecalc = true
          }
        }

        if (needsRecalc) {
          recalculateMarks()
        }
      }
    )
  }, [editor, recalculateMarks])

  // Register update listener for STRESS_OVERLAY_TAG
  useEffect(() => {
    return editor.registerUpdateListener(({ tags }) => {
      if (tags.has(STRESS_OVERLAY_TAG)) {
        recalculateMarks()
      }
    })
  }, [editor, recalculateMarks])

  // Register command listener for line stress metadata
  useEffect(() => {
    return editor.registerCommand(
      SET_LINE_STRESS_METADATA_COMMAND,
      (payload: SetLineStressMetadataPayload) => {
        setLineMetadata(payload.metadata)
        return false
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor])

  // Use autoUpdate to track scroll/resize and reposition marks
  useEffect(() => {
    const rootElement = editor.getRootElement()
    if (!rootElement || !containerRef.current) return

    const cleanup = autoUpdate(
      rootElement,
      containerRef.current,
      recalculateMarks,
      {
        elementResize: true,
        ancestorScroll: true,
        ancestorResize: true,
      }
    )

    return cleanup
  }, [editor, containerMounted, recalculateMarks])

  // Respect user preferences - hide when disabled
  const isEnabled = preferences.feedback.stress.enabled
  const showStressCount = preferences.feedback.stress.showStressCount
  if (!isEnabled) {
    return null
  }

  // Render marks as absolutely positioned elements
  return (
    <div
      ref={(el) => {
        containerRef.current = el
        overlayContainerRef.current = el
        setContainerMounted(!!el)
      }}
      className="stress-overlay-layer"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {/* SVG symbol definitions */}
      <StressMarkSymbols />

      {/* Stress marks - render only visible marks (virtualized) */}
      {visibleMarks.map((mark) => (
        <AccentMark
          key={mark.key}
          stressLevel={mark.stressLevel}
          x={mark.x}
          y={mark.y}
          syllable={mark.text}
          overridden={mark.overridden}
          nodeKey={mark.nodeKey}
          word={mark.word}
          syllableIndex={mark.syllableIndex}
        />
      ))}

      {/* Per-line stress count badges - gated on showStressCount preference */}
      {showStressCount && Object.entries(lineMetadata).map(([nodeKey, metadata]) => {
        if (!metadata || typeof metadata !== 'object') return null
        if (!('total_syllables' in metadata) || !('stressed_syllables' in metadata)) return null

        const nodeDom = editor.getElementByKey(nodeKey)
        if (!nodeDom) return null

        const rootElement = editor.getRootElement()
        if (!rootElement) return null

        const rootRect = rootElement.getBoundingClientRect()
        const nodeRect = nodeDom.getBoundingClientRect()

        // Right-align all syllable badges at consistent x position
        const editorWidth = rootRect.width
        const badgeWidth = 50  // Approximate width of (##/##) badge
        const rightMargin = 8  // Margin from editor edge
        const x = editorWidth - rightMargin - badgeWidth
        const y = nodeRect.top - rootRect.top

        const typedMetadata = metadata as {
          total_syllables: number
          stressed_syllables: number
          words: number
          expected_stressed_syllables?: number
        }

        // Get word breakdown data for tooltip
        const words = wordBreakdowns.get(nodeKey)

        return (
          <StressCountBadge
            key={`line-badge-${nodeKey}`}
            stressedCount={typedMetadata.stressed_syllables}
            totalCount={typedMetadata.total_syllables}
            expectedCount={typedMetadata.expected_stressed_syllables}
            words={words}
            x={x}
            y={y}
            nodeKey={nodeKey}
          />
        )
      })}
    </div>
  )
}
